import {
  DataProvider,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from 'react-admin'
import { CURRENT_USER_URL } from '../api-urls'
import {
  CacheableDataProviderExtensionResult,
  DataProviderExtensionResult,
} from '../common/data-provider'
import { get, getBlob, patch, post } from '../common/fetch.utils'
import { UserSettingsDto } from '../dto/user-settings.dto'
import { UserDto } from '../dto/user.dto'
import { DateTime } from '../common/date-time'

const provider = {
  getOne: async (
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    resource: string,
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    params: GetOneParams,
  ): Promise<GetOneResult<UserDto>> => {
    const data = await get<UserDto>(CURRENT_USER_URL)
    return Promise.resolve({
      data,
    })
  },
  update: async (
    resource: string,
    { data, previousData }: UpdateParams<UserDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<{ [n: string]: any }, UserDto>(
      CURRENT_USER_URL,
      {
        userName:
          data.userName !== previousData.userName ? data.userName : undefined,
      },
    )
    return Promise.resolve({ data: update })
  },
  getSettings: async (
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    resource: string,
  ): Promise<DataProviderExtensionResult<UserSettingsDto>> => {
    const settings = await get<UserSettingsDto>(
      `${CURRENT_USER_URL}/GetSettings/`,
    )
    return {
      data: settings,
    }
  },
  patchSettings: async (
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    resource: string,
    params: UserSettingsDto,
  ): Promise<DataProviderExtensionResult<UserSettingsDto>> => {
    const settings = await patch<UserSettingsDto, UserSettingsDto>(
      `${CURRENT_USER_URL}/PatchSettings/`,
      params,
    )
    return {
      data: settings,
    }
  },
  changePassword: async (
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    resource: string,
    params: ChangeUserPasswordRequest,
  ): Promise<DataProviderExtensionResult<ChangeUserPasswordResponse>> => {
    const url = `${CURRENT_USER_URL}/ChangePassword/`
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })

    // try to parse JSON body even for 400 responses
    let body: any = null
    try {
      body = await response.json()
    } catch {
      body = null
    }

    if (!response.ok && response.status !== 400) {
      const msg =
        body?.message || `Request failed with status ${response.status}`
      throw new Error(msg)
    }

    return {
      data: body as unknown as ChangeUserPasswordResponse,
    }
  },
  getAPIVersion: async (
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    resource: string,
  ): Promise<DataProviderExtensionResult<GetAPIVersionResponse>> => {
    const version = await get<GetAPIVersionResponse>(
      CURRENT_USER_URL,
      '/GetAPIVersion',
    )
    return {
      data: version as GetAPIVersionResponse,
    }
  },
  getChangelog: async (
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    resource: string,
  ): Promise<any> => {
    const blob = await getBlob(CURRENT_USER_URL, '/GetChangelog')
    return Promise.resolve({
      data: {
        blob,
      },
    })
  },
  getApplicationSettings: async (
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    resource: string,
  ): Promise<DataProviderExtensionResult<ApplicationSettings>> => {
    const settings = await get<ApplicationSettings>(
      `${CURRENT_USER_URL}/GetApplicationSettings/`,
    )
    return {
      data: settings,
    }
  },
  patchApplicationSettings: async (
    resource: string,
    params: ApplicationSettings,
  ): Promise<DataProviderExtensionResult<ApplicationSettings>> => {
    const settings = await patch<ApplicationSettings, ApplicationSettings>(
      `${CURRENT_USER_URL}/PatchApplicationSettings/`,
      params,
    )
    return {
      data: settings,
    }
  },
  getServerDateTime: async (
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    resource: string,
  ): Promise<DataProviderExtensionResult<GetServerDateTimeResponse>> => {
    const dateTime = await get<GetServerDateTimeResponse>(
      `${CURRENT_USER_URL}/GetServerDateTime/`,
    )
    return {
      data: dateTime,
    }
  },
  sendResetPasswordLink: async (
    resource: string,
    params: sendResetPasswordLinkRequest,
  ): Promise<Record<string, any>> => {
    await post<sendResetPasswordLinkRequest, Record<string, never>>(
      `${CURRENT_USER_URL}/SendResetPasswordLink`,
      params,
    )
    return {
      data: {},
    }
  },
  resetPassword: async (
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    resource: string,
    params: ResetPasswordRequest,
  ): Promise<DataProviderExtensionResult<ResetPasswordResponse>> => {
    const url = `${CURRENT_USER_URL}/ResetPassword/`
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })

    // try to parse JSON body even for 400 responses
    let body: any = null
    try {
      body = await response.json()
    } catch {
      body = null
    }

    if (!response.ok && response.status !== 400) {
      const msg =
        body?.message || `Request failed with status ${response.status}`
      throw new Error(msg)
    }

    return {
      data: body as unknown as ResetPasswordResponse,
    }
  },
} as UserDataProvider

interface UserDataProvider extends DataProvider {
  changePassword: (
    resource: string,
    params: ChangeUserPasswordRequest,
  ) => Promise<CacheableDataProviderExtensionResult<ChangeUserPasswordResponse>>

  getSettings: (
    resource: string,
  ) => Promise<CacheableDataProviderExtensionResult<UserSettingsDto>>

  patchSettings: (
    resource: string,
    params: UserSettingsDto,
  ) => Promise<CacheableDataProviderExtensionResult<UserSettingsDto>>

  getAPIVersion: (
    resource: string,
  ) => Promise<DataProviderExtensionResult<GetAPIVersionResponse>>

  getChangelog: (resource: string) => Promise<any>

  getApplicationSettings: (
    resource: string,
  ) => Promise<CacheableDataProviderExtensionResult<ApplicationSettings>>

  patchApplicationSettings: (
    resource: string,
    params: ApplicationSettings,
  ) => Promise<CacheableDataProviderExtensionResult<ApplicationSettings>>

  getServerDateTime: (
    resource: string,
  ) => Promise<CacheableDataProviderExtensionResult<GetServerDateTimeResponse>>

  sendResetPasswordLink: (
    resource: string,
    params: sendResetPasswordLinkRequest,
  ) => Promise<Record<string, any>>

  resetPassword: (
    resource: string,
    params: ResetPasswordRequest,
  ) => Promise<CacheableDataProviderExtensionResult<ResetPasswordResponse>>
}

interface ChangeUserPasswordRequest {
  readonly newPassword: string
}

interface ChangeUserPasswordResponse {
  readonly password: string
  readonly id: number
  readonly userName: string
}

interface ResetPasswordRequest {
  readonly userId: number
  readonly newPassword: string
}

interface ResetPasswordResponse {
  readonly password: string
  readonly userName: string
}

interface GetAPIVersionResponse {
  readonly version: string
}

interface ApplicationSettings {
  id: number
  patchTicketAuditComment: boolean
  patchBookingAuditComment: boolean
  patchAccessUsingAuditComment: boolean
}

interface GetServerDateTimeResponse {
  serverDateTime: DateTime
}

interface sendResetPasswordLinkRequest {
  readonly email: string
}

export default provider
